import React, { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { SupportedApps, connect } from './shared'
import axios from '../../../../../data/http/axios'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import FormError from '~/Common-Objects/UI/Forms/FormError/FormError'
import Hint from '~/Common-Objects/UI/Hint/Hint'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import SquareChip from '~/Common-Objects/UI/Chips/SquareChip.jsx'
import StickyNavBottom from '~/Common-Objects/Navigation/StickNavBottom'
import TierLimitHolder from '~/Common-Objects/UI/Cards/TierLimitHolder'
import Toggle from '~/Common-Objects/UI/Forms/Toggle/Toggle'
import i18n from '../../../../../i18n'
import useTierAllowed from '~/Common-Objects/hooks/useTierAllowed'
import PermissionChecker from '~/Classes/permissions'

export default function SocialCampaignSelectChannels({ campaign, refresh = () => {} }) {
    const history = useHistory()
    const site = useSelector((state) => state.siteMaster)
    const user = useSelector((state) => state.user)
    const tier = useSelector((state) => state.accountMaster?.tier)
    const permissionsStore = useSelector((state) => state.permission)
    const [apps, setApps] = useState(null)
    const [accApps, setAccApps] = React.useState(null)
    const [campaigns, setCampaigns] = useState(campaign.Campaigns || [])
    const [socialAccounts, setSocialAccounts] = useState(null)
    const [popupLinks, setPopupLinks] = useState(null)
    const [account, setAccount] = useState(null)
    const [canHaveMultiChannels, setCanHaveMultiChannels] = useState(tier?.permissions?.socialChannels?.limit > 1)
    const [selected, setSelected] = useState({
        LINKEDIN: campaign.Campaigns?.some((c) => c.options.AppCode === 'LINKEDIN'),
        FACEBOOK: campaign.Campaigns?.some((c) => c.options.AppCode === 'FACEBOOK'),
        INSTAGRAM: campaign.Campaigns?.some((c) => c.options.AppCode === 'INSTAGRAM'),
        TWITTER: campaign.Campaigns?.some((c) => c.options.AppCode === 'TWITTER')
    })
    const [loading, setLoading] = useState(false)
    const [warning, setWarning] = useState(null)
    const timer = useRef(null)
    const { requiredTier } = useTierAllowed('socialChannels', 4)

    const canConnect = PermissionChecker('company', permissionsStore.permissions, 'write')

    const getApps = async () => {
        const socialRes = await axios.get('/social')
        setSocialAccounts(socialRes.data)
        const res = await axios.get('/accountMaster/application?includeSocialAccounts=true')
        if (accApps && res.data.Applications.length > accApps.length) {
            if (timer.current) clearInterval(timer.current)
            return setAccApps(res.data.Applications)
        }
        if (!accApps) {
            setAccApps(res.data.Applications)
            // auto add provider for all the connected apps on first load
            if (!campaign.Campaigns.length && canHaveMultiChannels) {
                for (const app of SupportedApps) {
                    if (app.disabled) continue
                    if (res.data.Applications.some((a) => a.ApplicationId === app.id)) {
                        setSelected((prev) => ({ ...prev, [app.code]: true }))
                    }
                }
            }

            if (!campaign.Campaigns?.length && !canHaveMultiChannels && account?.options?.selectedSocialChannel) {
                // if there are no campaigns, and we are not allowed to have multiple channels,
                // add the selected social channel from the account options
                const code = account.options.selectedSocialChannel
                const app = SupportedApps.find((a) => a.code === code)
                if (app && res.data.Applications.some((a) => a.ApplicationId === app.id)) {
                    await addProvider(app.code, socialAccounts, true)
                    setSelected((prev) => ({ ...prev, [app.code]: true }))
                }
            }

            if (campaign.Campaigns?.length > 1 && !canHaveMultiChannels) {
                await Promise.all(campaign.Campaigns.map((c) => remove(c)))
                await refresh()
                setSelected({
                    LINKEDIN: false,
                    FACEBOOK: false,
                    INSTAGRAM: false,
                    TWITTER: false
                })
            }
        }
        if (!apps) {
            const res = await axios.get('/application')
            setApps(res.data.Applications.filter((a) => SupportedApps.some((sa) => sa.id === a.id)))
        }
    }

    const getLinks = async () => {
        const links = {}
        for (const app of apps) {
            const link = await connect(site, user, app)
            links[app.id] = link
        }
        setPopupLinks(links)
    }

    const startTimer = () => {
        timer.current = setInterval(() => getApps(), 2000)
    }

    const toggle = (appCode) => {
        setSelected((prev) => ({ ...prev, [appCode]: !prev[appCode] }))
    }

    const addProvider = async (code, socials, doRefresh) => {
        const existing = campaigns.some((c) => c.options.AppCode === code)
        if (!existing) {
            const templateRes = await axios.post('/template', {
                templateType: 30,
                templateName: campaign.campaignName + ' - ' + code,
                templateJson: {
                    content: campaign.Template.templateJson.content,
                    images: campaign.Template.templateJson.images
                }
            })
            const arr = socials || socialAccounts
            const social = arr ? arr.find((sa) => sa.type === code) : undefined
            await axios.post('/campaign', {
                type: 31,
                campaignName: campaign.campaignName + ' - ' + code,
                TemplateId: templateRes.data.Template.templateID,
                parentCampaignId: campaign.id,
                options: {
                    AppCode: code,
                    SocialAccountId: social?.id
                }
            })
            if (!canHaveMultiChannels) {
                const options = account.options || {}
                options.selectedSocialChannel = code
                setAccount({ ...account, options })
                // update account master to set the selected social channel
                await axios.put('/accountMaster', { ...account, options })
                const res = await axios.get('/accountMaster')
                setAccount(res.data.AccountMaster)
            }
            if (doRefresh) {
                await refresh()
            }
        }
    }

    const remove = async (campaign) => {
        await axios.delete('/campaign/' + campaign.id)
        await refresh()
    }

    const next = async () => {
        setLoading(true)
        setWarning(null)
        const promises = []
        const providers = Object.keys(selected)

        for (const provider of providers) {
            if (selected[provider]) {
                const found = campaigns.find((c) => c.options.AppCode === provider)
                if (!found) {
                    promises.push(addProvider(provider))
                }
            } else {
                const found = campaigns.find((c) => c.options.AppCode === provider)
                if (found) {
                    promises.push(remove(found))
                }
            }
        }

        try {
            await Promise.all(promises)
            await refresh()
            history.push(`/cp/campaigns/add/social/${campaign.id}/2`)
        } catch (error) {
            setWarning(
                <FormError className='no-marg'>
                    Something went wrong, please try again or contact support
                </FormError>
            )
            setLoading(false)
        }
    }

    useEffect(() => {
        axios.get('/accountMaster').then((res) => {
            setAccount(res.data.AccountMaster)
        })

        return function () {
            if (timer.current) clearInterval(timer.current)
        }
    }, [])

    useEffect(() => {
        if (account) {
            getApps()
        }
    }, [account])

    useEffect(() => {
        setCampaigns(campaign.Campaigns)
    }, [campaign.Campaigns])

    useEffect(() => {
        if (apps?.length) {
            getLinks()
        }
    }, [apps])

    useEffect(() => {
        setCanHaveMultiChannels(tier?.permissions?.socialChannels?.limit > 1)
    }, [tier])

    let submitDisabled = !Object.values(selected).some((s) => s)
    const selectedChannelCount = Object.values(selected).filter((s) => s).length

    return (
        <div>
            <div className='row'>
                <div className='col-xs-12 col-sm-12 col-md-8 col-lg-6 mar-b25'>
                    <div className='quickFlex' style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <h4>{i18n.t('social:channels.header')}</h4>
                        {!canHaveMultiChannels && (
                            <SquareChip>Channel Limit: {selectedChannelCount}/1</SquareChip>
                        )}
                    </div>
                </div>
            </div>
            {!apps || (!accApps && <LoadingIcons />)}
            {!!apps?.length && !popupLinks && <LoadingIcons />}
            <div className='row'>
                <div className='col-xs-12 col-sm-12 col-md-12 col-lg-6'>
                    {!!apps &&
                        !!accApps &&
                        !!popupLinks &&
                        SupportedApps.map((base, index) => {
                            const app = apps.find((a) => a.id === base.id)
                            const accApp = accApps.find((a) => a.ApplicationId === base.id)
                            const link = popupLinks?.[base.id]
                            return (
                                <div key={index}>
                                    <Channel
                                        loading={loading}
                                        link={link}
                                        base={base}
                                        app={app}
                                        accApp={accApp}
                                        campaigns={campaigns}
                                        canConnect={canConnect}
                                        connect={startTimer}
                                        add={toggle}
                                        remove={toggle}
                                        anySelected={Object.values(selected).some((s) => s)}
                                        selected={selected[base.code]}
                                        canHaveMultiChannels={canHaveMultiChannels}
                                        upsell={
                                            !canHaveMultiChannels &&
                                            account.options.selectedSocialChannel &&
                                            account.options.selectedSocialChannel !== base.code
                                        }
                                    />
                                </div>
                            )
                        })}
                </div>
            </div>

            {!canHaveMultiChannels && (
                <div className='row'>
                    <div className='col-xs-12 col-sm-12 col-md-12 col-lg-6'>
                        <TierLimitHolder
                            noMarg={true}
                            newLayout={true}
                            type='multiple social channels'
                            title='Stop limiting yourself to one network a month'
                            description='Upgrade your plan to post to all your connected channels and reach a wider audience. More visibility, engagement, and growth across all your channels.'
                            upsell={requiredTier}
                            feature='socialChannels'
                            onUpgrade={getApps}
                        />
                    </div>
                </div>
            )}

            {canHaveMultiChannels && (
                <Hint
                    className='mar-l0 mar-r0 mar-t20'
                    iconLeft={<Icons icon='questionCircle' />}
                    target='_blank'
                    href='https://transpond.io/support/creating-and-sending-campaigns/social-campaigns'
                >
                    {i18n.t('social:channels.help')}
                </Hint>
            )}

            <StickyNavBottom>
                <div className='quickFlex' style={{ flexDirection: 'row-reverse', alignItems: 'center', gap: 8 }}>
                    <Button
                        iconRight={<Icons icon='arrowRight' />}
                        className={'button-primary mar-l5'}
                        disabled={submitDisabled || loading}
                        onClick={next}
                    >
                        {i18n.t('campaigns:standard.nextStep')}
                    </Button>
                    {warning}
                </div>
            </StickyNavBottom>
        </div>
    )
}

function Channel({
    base,
    app,
    accApp,
    campaigns,
    link,
    upsell,
    selected,
    canHaveMultiChannels = true,
    anySelected = false,
    loading = false,
    canConnect = true,
    connect = () => {},
    add = () => {},
    remove = () => {}
}) {
    const toggle = async () => {
        if (selected) {
            remove(base.code)
        } else {
            add(base.code)
        }
    }

    const openPopup = function (url) {
        // Define the size and position of the popup window
        const width = 1400
        const height = 670
        const left = (window.innerWidth - width) / 2
        const top = (window.innerHeight - height) / 2

        // Open the popup window
        window.open(url, '', `height=${height},width=${width},left=${left},top=${top},toolbar=no,menubar=no,status=no`)

        connect()
    }

    let toggleDisabled = !!loading
    if (!canHaveMultiChannels && anySelected && !selected) toggleDisabled = true

    let cta
    if (base.disabled || app.disabled) {
        cta = <SquareChip>Unavailable</SquareChip>
    } else if (loading && accApp) {
        cta = <LoadingIcons style={{ width: 20, height: 20 }} />
    } else if (accApp && (canHaveMultiChannels || !campaigns.length)) {
        cta = <Toggle onChange={toggle} checked={selected} disabled={toggleDisabled} />
    } else if (!accApp && (canHaveMultiChannels || !campaigns.length)) {
        if (canConnect) {
            cta = (
                <Button target='_blank' className='button-primary button-sml' onClick={() => openPopup(link)}>
                    {i18n.t('social:channels.connect')}
                </Button>
            )
        } else {
            cta = (
                <Button
                    target='_blank'
                    disabled={true}
                    tooltip='Contact account admin to add a channel'
                    for={base.code + '_cantconnect'}
                    className='button-primary button-sml'
                    onClick={() => openPopup(link)}
                >
                    {i18n.t('social:channels.connect')}
                </Button>
            )
        }
    } else if (upsell) {
        cta = <Icons icon='padlock' style={{ height: 16, width: 16 }} color='var(--neutral-700)' />
    } else if (!canHaveMultiChannels && campaigns.length) {
        cta = <SquareChip className='square-chip-green'>Active</SquareChip>
    }
    return (
        <Paper style={{ padding: 16, marginBottom: 12 }} className='with-shadow'>
            <div className='quickFlex' style={{ alignItems: 'center' }}>
                <div className='mar-r20'>
                    {!!base.icon && (
                        <Icons
                            icon={base.icon}
                            style={{ height: 20, width: 20 }}
                            disabled={!selected && canHaveMultiChannels}
                        />
                    )}
                    {!base.icon && <img src={base.logo} style={{ height: 20 }} />}
                </div>
                <div style={{ flexGrow: 1 }}>
                    <div>
                        <strong>{i18n.t('social:channels.' + base.code)}</strong>
                    </div>
                    <div className='text-grey text-sml'>{i18n.t('social:channels.' + base.code + '_DESC')}</div>
                </div>
                <div>{cta}</div>
            </div>
        </Paper>
    )
}
